import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Trash2,
  ArrowRight,
  ArrowLeft,
  Tag,
  X,
  User,
  Award,
  Loader2,
  ShoppingBag,
  CheckCircle,
  Users,
  Minus,
  Plus,
  UserPlus,
  AlertCircle,
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Maximum participants allowed per cart item
const MAX_PARTICIPANTS = 99;

export default function CartPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { 
    cart, 
    loading, 
    removeFromCart, 
    clearCart, 
    updateItemQuantity,
    applyPromoCode, 
    removePromoCode 
  } = useCart();
  
  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Registration type per item: 'self', 'others', 'self_and_others'
  const [itemRegistrationTypes, setItemRegistrationTypes] = useState({});
  
  // Participants data - keyed by item key (item_id + variant)
  const [itemParticipants, setItemParticipants] = useState({});

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/cart');
    }
  }, [isAuthenticated, navigate]);

  // Initialize registration types and participants for each cart item
  useEffect(() => {
    if (cart.items.length > 0 && user) {
      cart.items.forEach((item) => {
        const key = `${item.item_id}-${item.variant}`;
        const quantity = item.quantity || 1;
        
        // Initialize registration type to 'self' by default
        if (!itemRegistrationTypes[key]) {
          setItemRegistrationTypes(prev => ({
            ...prev,
            [key]: 'self'
          }));
        }
        
        // Initialize participants based on quantity
        if (!itemParticipants[key] || itemParticipants[key].length !== quantity) {
          const participants = [];
          for (let i = 0; i < quantity; i++) {
            // First participant is auto-filled with user details when registration type is 'self'
            if (i === 0) {
              participants.push({
                id: Date.now() + i,
                first_name: user?.first_name || '',
                last_name: user?.last_name || '',
                email: user?.email || '',
                phone: user?.phone || '',
                is_self: true,
              });
            } else {
              participants.push({
                id: Date.now() + i,
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                is_self: false,
              });
            }
          }
          setItemParticipants(prev => ({
            ...prev,
            [key]: participants
          }));
        }
      });
    }
  }, [cart.items, user]);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    
    setApplyingPromo(true);
    const result = await applyPromoCode(promoInput);
    if (result.success) {
      setPromoInput('');
    }
    setApplyingPromo(false);
  };

  const handleRemoveItem = async (itemId, variant) => {
    await removeFromCart(itemId, variant);
    // Clear related state
    const key = `${itemId}-${variant}`;
    setItemParticipants(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
    setItemRegistrationTypes(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  };

  const handleQuantityChange = async (itemId, variant, newQuantity) => {
    const quantity = Math.max(1, Math.min(newQuantity, MAX_PARTICIPANTS));
    const key = `${itemId}-${variant}`;
    
    if (updateItemQuantity) {
      await updateItemQuantity(itemId, variant, quantity);
    }
    
    // Update participants array to match new quantity
    setItemParticipants(prev => {
      const current = prev[key] || [];
      const regType = itemRegistrationTypes[key] || 'self';
      
      if (quantity > current.length) {
        // Add more participants
        const newParticipants = [...current];
        for (let i = current.length; i < quantity; i++) {
          newParticipants.push({
            id: Date.now() + i,
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            is_self: false,
          });
        }
        return { ...prev, [key]: newParticipants };
      } else if (quantity < current.length) {
        // Remove extra participants
        return { ...prev, [key]: current.slice(0, quantity) };
      }
      return prev;
    });
    
    // If quantity goes back to 1 and type was 'self_and_others', switch to 'self'
    if (quantity === 1 && itemRegistrationTypes[key] === 'self_and_others') {
      setItemRegistrationTypes(prev => ({
        ...prev,
        [key]: 'self'
      }));
    }
  };

  const handleRegistrationTypeChange = (key, type) => {
    setItemRegistrationTypes(prev => ({
      ...prev,
      [key]: type
    }));
    
    // Update first participant based on type
    setItemParticipants(prev => {
      const current = prev[key] || [];
      if (current.length === 0) return prev;
      
      const updated = [...current];
      if (type === 'self' || type === 'self_and_others') {
        // Auto-fill first participant with user details
        updated[0] = {
          ...updated[0],
          first_name: user?.first_name || '',
          last_name: user?.last_name || '',
          email: user?.email || '',
          phone: user?.phone || '',
          is_self: true,
        };
      } else if (type === 'others') {
        // Clear first participant's self flag
        updated[0] = {
          ...updated[0],
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          is_self: false,
        };
      }
      return { ...prev, [key]: updated };
    });
  };

  const updateParticipant = (key, index, field, value) => {
    setItemParticipants(prev => {
      const current = prev[key] || [];
      const updated = [...current];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return { ...prev, [key]: updated };
    });
    
    // Clear validation error for this field
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${key}-${index}-${field}`];
      return newErrors;
    });
  };

  const formatVariant = (variant) => {
    const legacyVariants = {
      'live': 'Live Access',
      'recorded': 'Download Access',
      'digital_download': 'USB Flash Drive',
      'group_live': 'Group Live (Up to 20)',
      'live_recorded': 'Live + Download',
      'live_usb': 'Live + USB',
    };
    return legacyVariants[variant] || variant;
  };

  const getSpeakerNames = (item) => {
    if (item.speakers && item.speakers.length > 0) {
      return item.speakers.map(s => s.name).join(', ');
    }
    return null;
  };

  const validateParticipants = () => {
    const errors = {};
    let hasErrors = false;
    
    cart.items.forEach((item) => {
      const key = `${item.item_id}-${item.variant}`;
      const participants = itemParticipants[key] || [];
      const regType = itemRegistrationTypes[key] || 'self';
      
      participants.forEach((participant, index) => {
        // For 'others' type, all participants need details
        // For 'self' type, first participant (self) needs details
        // For 'self_and_others' type, all participants need details
        
        if (!participant.first_name?.trim()) {
          errors[`${key}-${index}-first_name`] = 'First name is required';
          hasErrors = true;
        }
        if (!participant.last_name?.trim()) {
          errors[`${key}-${index}-last_name`] = 'Last name is required';
          hasErrors = true;
        }
        if (!participant.email?.trim()) {
          errors[`${key}-${index}-email`] = 'Email is required';
          hasErrors = true;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participant.email)) {
          errors[`${key}-${index}-email`] = 'Invalid email format';
          hasErrors = true;
        }
      });
    });
    
    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleProceedToCheckout = () => {
    // Validate all participant forms
    if (!validateParticipants()) {
      toast.error('Please fill in all required participant details');
      // Expand the first item with errors
      const firstErrorKey = Object.keys(validationErrors)[0];
      if (firstErrorKey) {
        const [itemId, variant] = firstErrorKey.split('-');
        setExpandedItem(`${itemId}-${variant}`);
      }
      return;
    }
    
    // Store participant data in session storage for checkout page
    sessionStorage.setItem('checkoutParticipants', JSON.stringify(itemParticipants));
    sessionStorage.setItem('checkoutRegistrationTypes', JSON.stringify(itemRegistrationTypes));
    navigate('/checkout');
  };

  if (loading && cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-50 pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pt-28 pb-12" data-testid="cart-page">
      <div className="container-custom">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-zinc-950">Shopping Cart</h1>
            <p className="text-zinc-500 mt-1">
              {cart.items.length} {cart.items.length === 1 ? 'item' : 'items'} in your cart
            </p>
          </div>
          {cart.items.length > 0 && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={clearCart}
              data-testid="clear-cart-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Cart
            </Button>
          )}
        </div>

        {cart.items.length === 0 ? (
          /* Empty Cart State */
          <Card className="border-zinc-200 rounded-xl text-center py-16">
            <CardContent>
              <div className="w-20 h-20 mx-auto bg-zinc-100 rounded-full flex items-center justify-center mb-6">
                <ShoppingBag className="w-10 h-10 text-zinc-400" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-zinc-950 mb-3">
                Your cart is empty
              </h2>
              <p className="text-zinc-500 mb-8 max-w-md mx-auto">
                Browse our webinars and seminars to find the perfect training for your needs.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/webinars">
                  <Button className="bg-zinc-950 hover:bg-zinc-800">
                    Browse Webinars
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/seminars">
                  <Button variant="outline" className="border-zinc-900 text-zinc-900">
                    Browse Seminars
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Cart with Items */
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items - Left Side */}
            <div className="lg:col-span-2 space-y-4">
              <Accordion 
                type="single" 
                collapsible 
                value={expandedItem}
                onValueChange={setExpandedItem}
                className="space-y-4"
              >
                {cart.items.map((item, index) => {
                  const key = `${item.item_id}-${item.variant}`;
                  const quantity = item.quantity || 1;
                  const speakerNames = getSpeakerNames(item);
                  const registrationType = itemRegistrationTypes[key] || 'self';
                  const participants = itemParticipants[key] || [];
                  const filledCount = participants.filter(p => p.first_name && p.email).length;
                  
                  return (
                    <AccordionItem 
                      key={key} 
                      value={key}
                      className="border border-zinc-200 rounded-xl overflow-hidden bg-white"
                      data-testid={`cart-item-${index}`}
                    >
                      {/* Simplified Item Header - No Thumbnail */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={
                                item.item_type === 'bundle'
                                  ? 'bg-emerald-100 text-emerald-700 text-xs'
                                  : 'bg-zinc-100 text-zinc-950 text-xs'
                              }>
                                {item.item_type === 'bundle' ? 'Bundle' : item.item_type === 'webinar' ? 'Webinar' : 'Seminar'}
                              </Badge>
                              {item.item_type === 'bundle' && item.webinar_count != null && (
                                <span className="text-xs text-zinc-500">{item.webinar_count} webinars included</span>
                              )}
                            </div>
                            
                            {/* Title */}
                            <h3 className="font-semibold text-zinc-950 text-lg leading-tight mb-2">
                              {item.title}
                            </h3>
                            
                            {/* Variant & Speaker & Webinar Code */}
                            <div className="flex flex-col gap-1 text-sm text-zinc-600">
                              <span className="flex items-center gap-1">
                                <Tag className="w-4 h-4 text-zinc-400" />
                                {item.item_type === 'bundle'
                                  ? (item.variant === 'usb' ? 'USB Flash Drive' : 'Download Access')
                                  : formatVariant(item.variant)}
                              </span>
                              {speakerNames && (
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4 text-zinc-400" />
                                  {speakerNames}
                                </span>
                              )}
                              {item.webinar_code && (
                                <span className="text-xs text-zinc-500">
                                  Code: {item.webinar_code}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Quantity & Price */}
                          <div className="flex flex-col items-end gap-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-zinc-400 hover:text-red-600 -mt-1"
                              onClick={() => handleRemoveItem(item.item_id, item.variant)}
                              data-testid={`remove-item-${index}`}
                            >
                              <X className="w-5 h-5" />
                            </Button>
                            
                            {/* Quantity Selector */}
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-zinc-500">Qty:</Label>
                              <div className="flex items-center border border-zinc-200 rounded-lg">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleQuantityChange(item.item_id, item.variant, quantity - 1)}
                                  disabled={quantity <= 1}
                                  data-testid={`qty-minus-${index}`}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="w-8 text-center text-sm font-medium" data-testid={`qty-value-${index}`}>
                                  {quantity}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleQuantityChange(item.item_id, item.variant, quantity + 1)}
                                  disabled={quantity >= MAX_PARTICIPANTS}
                                  data-testid={`qty-plus-${index}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            <p className="text-xl font-bold text-zinc-950">
                              ${(item.price * quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        {/* Accordion Trigger for Participant Details */}
                        <AccordionTrigger className="mt-4 pt-4 border-t border-zinc-100 hover:no-underline">
                          <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                            <Users className="w-4 h-4" />
                            Add Participant Details ({filledCount}/{quantity})
                            {Object.keys(validationErrors).some(k => k.startsWith(key)) && (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </AccordionTrigger>
                      </div>

                      {/* Collapsible Participant Details */}
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-6 pt-2">
                          {/* Registration Type Selector */}
                          <div className="space-y-3">
                            <Label className="flex items-center gap-2 text-zinc-950">
                              <Users className="w-4 h-4" />
                              Who is this registration for?
                            </Label>
                            <p className="text-sm text-zinc-500">
                              Select whether you're registering for yourself, others, or both
                            </p>
                            
                            <RadioGroup 
                              value={registrationType} 
                              onValueChange={(val) => handleRegistrationTypeChange(key, val)}
                              className="space-y-2"
                            >
                              <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                registrationType === 'self' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                              }`}>
                                <RadioGroupItem value="self" id={`${key}-self`} />
                                <Label htmlFor={`${key}-self`} className="flex-1 cursor-pointer">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-zinc-900" />
                                    <span className="font-medium">Register for Myself</span>
                                  </div>
                                  <p className="text-sm text-zinc-500 mt-1">I will be attending this webinar</p>
                                </Label>
                              </div>
                              
                              <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                registrationType === 'others' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                              }`}>
                                <RadioGroupItem value="others" id={`${key}-others`} />
                                <Label htmlFor={`${key}-others`} className="flex-1 cursor-pointer">
                                  <div className="flex items-center gap-2">
                                    <UserPlus className="w-4 h-4 text-zinc-900" />
                                    <span className="font-medium">Register for Others</span>
                                  </div>
                                  <p className="text-sm text-zinc-500 mt-1">I am registering on behalf of someone else</p>
                                </Label>
                              </div>
                              
                              {/* Show "Self and Others" only when quantity > 1 */}
                              {quantity > 1 && (
                                <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                  registrationType === 'self_and_others' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                                }`}>
                                  <RadioGroupItem value="self_and_others" id={`${key}-self_and_others`} />
                                  <Label htmlFor={`${key}-self_and_others`} className="flex-1 cursor-pointer">
                                    <div className="flex items-center gap-2">
                                      <Users className="w-4 h-4 text-zinc-900" />
                                      <span className="font-medium">Register for Self & Others</span>
                                    </div>
                                    <p className="text-sm text-zinc-500 mt-1">I will attend along with {quantity - 1} other participant(s)</p>
                                  </Label>
                                </div>
                              )}
                            </RadioGroup>
                          </div>
                          
                          {/* Participant Forms in Accordion */}
                          <div className="space-y-3">
                            <Label className="text-zinc-950">Participant Details</Label>
                            <Accordion type="multiple" className="space-y-2">
                              {participants.map((participant, pIndex) => {
                                const isSelfSlot = (registrationType === 'self' && pIndex === 0) || 
                                                   (registrationType === 'self_and_others' && pIndex === 0);
                                const hasError = Object.keys(validationErrors).some(k => k.startsWith(`${key}-${pIndex}`));
                                
                                return (
                                  <AccordionItem 
                                    key={participant.id} 
                                    value={`participant-${pIndex}`}
                                    className={`border rounded-lg ${hasError ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}
                                  >
                                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                          participant.first_name && participant.email 
                                            ? 'bg-green-100 text-green-700' 
                                            : 'bg-zinc-100 text-zinc-500'
                                        }`}>
                                          {pIndex + 1}
                                        </div>
                                        <span className="font-medium text-zinc-950">
                                          {isSelfSlot ? 'Myself' : `Participant ${pIndex + 1}`}
                                        </span>
                                        {participant.first_name && (
                                          <span className="text-zinc-500">
                                            - {participant.first_name} {participant.last_name}
                                          </span>
                                        )}
                                        {participant.first_name && participant.email && (
                                          <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                                        )}
                                        {hasError && (
                                          <AlertCircle className="w-4 h-4 text-red-500 ml-auto" />
                                        )}
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 pb-4">
                                      <div className="grid grid-cols-2 gap-4 pt-2">
                                        <div className="space-y-2">
                                          <Label htmlFor={`${key}-${pIndex}-first_name`}>
                                            First Name <span className="text-red-500">*</span>
                                          </Label>
                                          <Input
                                            id={`${key}-${pIndex}-first_name`}
                                            value={participant.first_name}
                                            onChange={(e) => updateParticipant(key, pIndex, 'first_name', e.target.value)}
                                            placeholder="Enter first name"
                                            className={validationErrors[`${key}-${pIndex}-first_name`] ? 'border-red-500' : ''}
                                          />
                                          {validationErrors[`${key}-${pIndex}-first_name`] && (
                                            <p className="text-xs text-red-500">{validationErrors[`${key}-${pIndex}-first_name`]}</p>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          <Label htmlFor={`${key}-${pIndex}-last_name`}>
                                            Last Name <span className="text-red-500">*</span>
                                          </Label>
                                          <Input
                                            id={`${key}-${pIndex}-last_name`}
                                            value={participant.last_name}
                                            onChange={(e) => updateParticipant(key, pIndex, 'last_name', e.target.value)}
                                            placeholder="Enter last name"
                                            className={validationErrors[`${key}-${pIndex}-last_name`] ? 'border-red-500' : ''}
                                          />
                                          {validationErrors[`${key}-${pIndex}-last_name`] && (
                                            <p className="text-xs text-red-500">{validationErrors[`${key}-${pIndex}-last_name`]}</p>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          <Label htmlFor={`${key}-${pIndex}-email`}>
                                            Email <span className="text-red-500">*</span>
                                          </Label>
                                          <Input
                                            id={`${key}-${pIndex}-email`}
                                            type="email"
                                            value={participant.email}
                                            onChange={(e) => updateParticipant(key, pIndex, 'email', e.target.value)}
                                            placeholder="Enter email address"
                                            className={validationErrors[`${key}-${pIndex}-email`] ? 'border-red-500' : ''}
                                          />
                                          {validationErrors[`${key}-${pIndex}-email`] && (
                                            <p className="text-xs text-red-500">{validationErrors[`${key}-${pIndex}-email`]}</p>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          <Label htmlFor={`${key}-${pIndex}-phone`}>Phone (Optional)</Label>
                                          <Input
                                            id={`${key}-${pIndex}-phone`}
                                            value={participant.phone}
                                            onChange={(e) => updateParticipant(key, pIndex, 'phone', e.target.value)}
                                            placeholder="Enter phone number"
                                          />
                                        </div>
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                );
                              })}
                            </Accordion>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              {/* Continue Shopping */}
              <div className="pt-4">
                <Link to="/webinars">
                  <Button variant="ghost" className="text-zinc-900">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </div>

            {/* Order Summary - Right Side */}
            <div className="lg:col-span-1">
              <Card className="border-zinc-200 rounded-xl sticky top-28">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-zinc-950">Order Summary</h3>
                  
                  {/* Promo Code */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Promo Code
                    </label>
                    {cart.promo_code ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-green-700" data-testid="applied-promo-code">{cart.promo_code}</p>
                            <p className="text-sm text-green-600" data-testid="applied-promo-discount">
                              {cart.promo_details?.type === 'percentage' 
                                ? `${cart.promo_details.value}% off` 
                                : cart.promo_details?.value != null
                                  ? `$${cart.promo_details.value} off`
                                  : 'Applied'}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-red-600"
                          onClick={removePromoCode}
                          data-testid="remove-promo-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          className="flex-1"
                          data-testid="promo-input"
                        />
                        <Button
                          onClick={handleApplyPromo}
                          disabled={applyingPromo || !promoInput.trim()}
                          className="bg-zinc-950 hover:bg-zinc-800"
                          data-testid="apply-promo-btn"
                        >
                          {applyingPromo ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Apply'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Price Breakdown */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">Subtotal</span>
                      <span className="font-medium">${cart.subtotal?.toFixed(2) || '0.00'}</span>
                    </div>
                    {cart.discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span data-testid="cart-discount-amount">-${cart.discount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Total */}
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-zinc-950">Total</span>
                    <span className="text-2xl font-bold text-zinc-950">
                      ${cart.total?.toFixed(2) || '0.00'}
                    </span>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold py-6 rounded-xl"
                    onClick={handleProceedToCheckout}
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>

                  {/* Trust Badge */}
                  <div className="pt-4 flex items-center justify-center text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Secure Checkout
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
